import React, { useCallback, useEffect, useRef, useState } from "react";

import { Component } from "../../../../commons/types/component";
import cn from "../../libs/class-name";

type Width = "s" | "base" | "l" | "xl" | "full" | "auto";

interface Props extends Component {
  children: React.ReactNode;
  footer?: React.ReactNode;
  actionButtons?: React.ReactElement;
  backButton?: React.ReactElement;
  closeButton?: React.ReactElement;
  backgroundImage?: string;
  width?: Width;
  layout?: "base" | "noPadding";
  nodeRef?: React.Ref<HTMLElement>;
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
  onTouchStart?: React.TouchEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
}

export const ModalContext = React.createContext<{ scroll: boolean }>({
  scroll: false
});

const Modal = ({
  classNames = [],
  children,
  footer = null,
  actionButtons = undefined,
  backButton = undefined,
  closeButton = undefined,
  backgroundImage = "",
  width = "base",
  layout = "base",
  nodeRef = null,
  onMouseDown,
  onTouchStart,
  onKeyDown
}: Props) => {
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const childrenRef = useRef<HTMLDivElement>(null);

  const updateScroll = useCallback(() => {
    const node = childrenRef.current;

    if (node) {
      const newCanScrollDown = node.scrollTop + node.clientHeight < node.scrollHeight;
      const newCanScrollUp = node.scrollTop > 0;

      if (canScrollDown !== newCanScrollDown) {
        setCanScrollDown(newCanScrollDown);
      }
      if (canScrollUp !== newCanScrollUp) {
        setCanScrollUp(newCanScrollUp);
      }
    }
  }, [canScrollDown, canScrollUp]);

  useEffect(() => {
    const node = childrenRef.current;

    updateScroll();
    node?.addEventListener("scroll", updateScroll);

    // Watch for content size changes (e.g., async data loading, window resizes)
    const resizeObserver = new ResizeObserver(() => {
      updateScroll();
    });

    if (node) {
      resizeObserver.observe(node);
    }

    return () => {
      node?.removeEventListener("scroll", updateScroll);
      resizeObserver.disconnect();
    };
  }, [updateScroll]);

  const scroll = canScrollUp || canScrollDown;

  const style: React.CSSProperties = {};
  if (backgroundImage) {
    style.backgroundImage = `url(${backgroundImage})`;
  }

  const header = !!backButton || !!actionButtons || !!closeButton;

  return (
    <section
      ref={nodeRef}
      className={cn(
        "Modal",
        [
          {
            [`width-${width}`]: width,
            [`layout-${layout}`]: layout,
            header,
            scroll,
            canScrollUp,
            canScrollDown
          }
        ],
        classNames
      )}
      style={style}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onKeyDown={onKeyDown}
    >
      {header && (
        <div className="Modal__header">
          <div className="Modal__back">{backButton}</div>
          <div className="Modal__actions">
            {actionButtons && <div className="Modal__actionButtons">{actionButtons}</div>}
            {closeButton && <div className="Modal__close">{closeButton}</div>}
          </div>
        </div>
      )}
      <div className="Modal__scroll">
        <div className="Modal__children" ref={childrenRef}>
          <ModalContext value={{ scroll }}>{children}</ModalContext>
        </div>
      </div>

      {footer && <div className="Modal__footer">{footer}</div>}
    </section>
  );
};

export default Modal;
